import InDocDiagramFrame from './InDocDiagramFrame';
import { inDocPalette, inDocType } from './inDocPalette';
import {
  InDocDefs,
  InDocGrid,
  InDocSectionLabel,
  InDocPanel,
  InDocBus,
  InDocConnector,
} from './inDocDiagramKit';

const PREFIX = 'indocHero';

const lanes = [
  {
    id: 'corpus',
    x: 108,
    title: 'LIBRARY',
    sub: 'Indexed Files',
    tags: ['Keyword', 'Vector'],
    note: 'Role-Scoped',
  },
  {
    id: 'core',
    x: 360,
    title: 'INDOC CORE',
    sub: 'The Model Chooses',
    tags: ['Plan', 'Revise'],
    note: 'Budget Cap On',
    active: true,
  },
  {
    id: 'brief',
    x: 612,
    title: 'CITED BRIEF',
    sub: 'Answer + Ledger',
    tags: ['Source', 'Trace'],
    note: 'Sources Named',
  },
];

const ingestSteps = ['SCAN', 'OCR', 'EMBED', 'INDEX'];

const InDocHeroDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const c = inDocPalette(isDark);

  return (
    <InDocDiagramFrame
      theme={theme}
      caption="The Library · The Loop · The Brief With Sources"
      className="indoc-hero-frame"
    >
      <svg
        viewBox="0 0 720 440"
        className="w-full h-auto indoc-hero-svg"
        role="img"
        aria-label="inDoc: the document library, the investigation loop, and a brief that cites its sources"
      >
        <InDocDefs prefix={PREFIX} isDark={isDark} />
        <rect width="720" height="440" fill={c.bg} />
        <InDocGrid width={720} height={440} c={c} />

        <InDocSectionLabel x={360} y={36} c={c}>
          THE AGENT WORKS THE LIBRARY
        </InDocSectionLabel>

        {lanes.map(({ id, x, title, sub, tags, note, active }, laneIdx) => (
          <InDocPanel
            key={id}
            x={x - 88}
            y={72}
            w={176}
            h={148}
            prefix={PREFIX}
            c={c}
            active={active}
            className={`indoc-hero-lane indoc-hero-lane-${laneIdx}`}
          >
            <text
              x={x}
              y={100}
              textAnchor="middle"
              fill={active ? c.accent : c.label}
              {...inDocType.title}
            >
              {title}
            </text>
            <text
              x={x}
              y={118}
              textAnchor="middle"
              fill={c.muted}
              {...inDocType.sub}
            >
              {sub}
            </text>
            <line
              x1={x - 64}
              y1={128}
              x2={x + 64}
              y2={128}
              stroke={c.grid}
              strokeWidth="1"
            />
            {tags.map((tag, i) => (
              <g key={tag}>
                <rect
                  x={x - 70 + i * 74}
                  y={138}
                  width="66"
                  height="22"
                  rx="4"
                  fill="none"
                  stroke={c.panelStroke}
                  strokeWidth="1"
                />
                <text
                  x={x - 37 + i * 74}
                  y={153}
                  textAnchor="middle"
                  fill={c.mono}
                  {...inDocType.monoSm}
                >
                  {tag}
                </text>
              </g>
            ))}
            <text
              x={x}
              y={198}
              textAnchor="middle"
              fill={c.muted}
              {...inDocType.monoSm}
            >
              {note}
            </text>
          </InDocPanel>
        ))}

        <InDocConnector
          x1={196}
          y1={146}
          x2={272}
          y2={146}
          c={c}
          prefix={PREFIX}
        />
        <InDocConnector
          x1={448}
          y1={146}
          x2={524}
          y2={146}
          c={c}
          prefix={PREFIX}
        />

        <circle
          cx="360"
          cy="146"
          r="46"
          fill="none"
          stroke={`url(#${PREFIX}-core)`}
          strokeWidth="1.5"
          className="indoc-hero-core-ring"
        />

        <InDocBus
          x1={80}
          y1={248}
          x2={640}
          y2={248}
          prefix={PREFIX}
          className="indoc-hero-bus"
        />
        <circle
          r="5"
          fill={c.accent}
          filter={`url(#${PREFIX}-glow)`}
          className="indoc-hero-bus-dot"
        >
          <animateMotion
            dur="7s"
            repeatCount="indefinite"
            path="M 80 248 L 640 248"
          />
        </circle>
        <text
          x={360}
          y={232}
          textAnchor="middle"
          fill={c.accent}
          {...inDocType.section}
        >
          ACCESS LINE
        </text>
        <text
          x={360}
          y={268}
          textAnchor="middle"
          fill={c.mono}
          {...inDocType.mono}
        >
          RBAC · ABAC · Tool-Bound · Not A Prompt Rule
        </text>

        {ingestSteps.map((step, i) => {
          const x = 150 + i * 140;
          return (
            <g key={step} className={`indoc-hero-step indoc-hero-step-${i}`}>
              <circle
                cx={x}
                cy={296}
                r="5"
                fill={c.accent}
                filter={`url(#${PREFIX}-glow)`}
                className="indoc-hero-step-dot"
              />
              <text
                x={x}
                y={318}
                textAnchor="middle"
                fill={c.label}
                {...inDocType.monoSm}
              >
                {step}
              </text>
            </g>
          );
        })}

        <InDocPanel x={48} y={340} w={624} h={72} prefix={PREFIX} c={c}>
          <text x={72} y={364} fill={c.muted} {...inDocType.section}>
            CONTROLS
          </text>
          {[
            'JWT · MFA',
            'Field Encrypt',
            'SIEM Export',
            'HIPAA Mode',
            'PCI Mode',
          ].map((item, i) => (
            <g
              key={item}
              className={`indoc-hero-control indoc-hero-control-${i}`}
            >
              <rect
                x={72 + i * 118}
                y={376}
                width="108"
                height="24"
                rx="4"
                fill="none"
                stroke={c.panelStroke}
                strokeWidth="1"
              />
              <text
                x={126 + i * 118}
                y={392}
                textAnchor="middle"
                fill={c.label}
                {...inDocType.sub}
              >
                {item}
              </text>
            </g>
          ))}
        </InDocPanel>
      </svg>
    </InDocDiagramFrame>
  );
};

export default InDocHeroDiagram;
